import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
  Alert,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  CircularProgress,
  SelectChangeEvent,
  OutlinedInput,
  Checkbox,
  ListItemText,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';

import { getApiErrorMessage } from '../../../utils/errorMessage';

import {
  type ShiftDto,
  type ShiftScope,
  type ShiftUpsertDto,
  type BranchWorkingHour,
  type RosterResultDto,
  getBranchShifts,
  createShift,
  updateShift,
  deleteShift,
  updateDefaultShift,
  bulkAssignRoster,
} from 'src/api/branch_api_data';
import { type EmployeeDto, getBranchEmployees } from 'src/api/api_data';

import {
  WorkingHoursGrid,
  getDefaultWorkingHours,
  normalizeWorkingHours,
  validateWorkingHours,
} from './WorkingHoursGrid';

// ----------------------------------------------------------------------

interface BranchShiftsTabProps {
  branchId: string;
  branchName?: string;
}

const SCOPE_LABELS: Record<ShiftScope, string> = {
  SHARED: 'Paylaşılan',
  PERSONAL: 'Kişisel',
};

const WORKING_HOURS_ERROR = 'Açık günlerde açılış ve kapanış saati farklı olmalıdır.';

interface ShiftFormState {
  open: boolean;
  shift: ShiftDto | null; // null => yeni vardiya
  name: string;
  scope: ShiftScope;
  workingHours: BranchWorkingHour[];
  submitting: boolean;
  error: string;
}

const emptyForm: ShiftFormState = {
  open: false,
  shift: null,
  name: '',
  scope: 'SHARED',
  workingHours: getDefaultWorkingHours(),
  submitting: false,
  error: '',
};

interface DeleteState {
  open: boolean;
  shift: ShiftDto | null;
  submitting: boolean;
}

const todayStr = () => new Date().toISOString().slice(0, 10);

interface RosterState {
  open: boolean;
  shiftId: string;
  employeeIds: string[];
  effectiveFrom: string;
  effectiveTo: string;
  submitting: boolean;
  error: string;
  results: RosterResultDto['results'] | null;
}

const emptyRoster: RosterState = {
  open: false,
  shiftId: '',
  employeeIds: [],
  effectiveFrom: todayStr(),
  effectiveTo: '',
  submitting: false,
  error: '',
  results: null,
};

const BranchShiftsTab: React.FC<BranchShiftsTabProps> = ({ branchId }) => {
  const [shifts, setShifts] = useState<ShiftDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const [form, setForm] = useState<ShiftFormState>(emptyForm);
  const [deleteState, setDeleteState] = useState<DeleteState>({
    open: false,
    shift: null,
    submitting: false,
  });

  const [roster, setRoster] = useState<RosterState>(emptyRoster);
  const [employees, setEmployees] = useState<EmployeeDto[]>([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);

  const loadShifts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getBranchShifts(branchId);
      setShifts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Vardiyalar yüklenemedi:', err);
      setError('Vardiyalar yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);

  // ----- Form aç/kapat -----

  const openCreate = () => {
    setForm({
      ...emptyForm,
      open: true,
      workingHours: getDefaultWorkingHours(),
    });
  };

  const openEdit = (shift: ShiftDto) => {
    setForm({
      open: true,
      shift,
      name: shift.name,
      scope: shift.scope,
      workingHours: normalizeWorkingHours(shift.workingHours),
      submitting: false,
      error: '',
    });
  };

  const closeForm = () => {
    if (form.submitting) return;
    setForm((prev) => ({ ...prev, open: false }));
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, name: event.target.value }));
  };

  const handleScopeChange = (event: SelectChangeEvent<ShiftScope>) => {
    setForm((prev) => ({ ...prev, scope: event.target.value as ShiftScope }));
  };

  const handleHoursChange = (workingHours: BranchWorkingHour[]) => {
    setForm((prev) => ({ ...prev, workingHours }));
  };

  const isDefaultForm = !!form.shift?.isDefault;

  const handleSubmit = async () => {
    if (!isDefaultForm && !form.name.trim()) {
      setForm((prev) => ({ ...prev, error: 'Vardiya adı zorunludur.' }));
      return;
    }
    if (!validateWorkingHours(form.workingHours)) {
      setForm((prev) => ({ ...prev, error: WORKING_HOURS_ERROR }));
      return;
    }

    const dto: ShiftUpsertDto = {
      name: form.name.trim(),
      scope: form.scope,
      workingHours: form.workingHours,
    };

    try {
      setForm((prev) => ({ ...prev, submitting: true, error: '' }));

      if (form.shift?.isDefault) {
        await updateDefaultShift(branchId, dto);
        toast.success('Varsayılan vardiya güncellendi.');
      } else if (form.shift) {
        await updateShift(branchId, form.shift.id, dto);
        toast.success('Vardiya güncellendi.');
      } else {
        await createShift(branchId, dto);
        toast.success('Vardiya oluşturuldu.');
      }

      setForm((prev) => ({ ...prev, open: false, submitting: false }));
      await loadShifts();
    } catch (err) {
      console.error('Vardiya kaydedilemedi:', err);
      setForm((prev) => ({
        ...prev,
        submitting: false,
        error: 'Vardiya kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.',
      }));
    }
  };

  // ----- Silme -----

  const openDelete = (shift: ShiftDto) => {
    setDeleteState({ open: true, shift, submitting: false });
  };

  const closeDelete = () => {
    if (deleteState.submitting) return;
    setDeleteState({ open: false, shift: null, submitting: false });
  };

  const handleDelete = async () => {
    if (!deleteState.shift) return;
    try {
      setDeleteState((prev) => ({ ...prev, submitting: true }));
      await deleteShift(branchId, deleteState.shift.id);
      toast.success('Vardiya silindi.');
      setDeleteState({ open: false, shift: null, submitting: false });
      await loadShifts();
    } catch (err: any) {
      console.error('Vardiya silinemedi:', err);
      // Backend ErrorResponse: "A072" referansi errorRef alaninda doner
      // (errorCode legacy string tasir). Bkz. GlobalExceptionHandler.buildResponse.
      toast.error(getApiErrorMessage(err));
      setDeleteState((prev) => ({ ...prev, submitting: false }));
    }
  };

  // ----- Toplu Atama (Roster) -----

  const openRoster = async () => {
    setRoster({ ...emptyRoster, open: true });
    try {
      setEmployeesLoading(true);
      const page = await getBranchEmployees(branchId, 0, 200);
      setEmployees(Array.isArray(page.content) ? page.content : []);
    } catch (err) {
      console.error('Çalışanlar yüklenemedi:', err);
      toast.error('Çalışan listesi yüklenemedi.');
    } finally {
      setEmployeesLoading(false);
    }
  };

  const closeRoster = () => {
    if (roster.submitting) return;
    setRoster((prev) => ({ ...prev, open: false }));
  };

  const handleRosterShiftChange = (event: SelectChangeEvent<string>) => {
    setRoster((prev) => ({ ...prev, shiftId: event.target.value }));
  };

  const handleRosterEmployeesChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setRoster((prev) => ({
      ...prev,
      employeeIds: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const employeeName = (id: string): string => {
    const emp = employees.find((e) => e.id === id);
    return emp ? `${emp.firstName} ${emp.lastName}` : id;
  };

  const handleRosterSubmit = async () => {
    if (!roster.shiftId) {
      setRoster((prev) => ({ ...prev, error: 'Lütfen bir vardiya seçin.' }));
      return;
    }
    if (roster.employeeIds.length === 0) {
      setRoster((prev) => ({ ...prev, error: 'En az bir çalışan seçin.' }));
      return;
    }
    if (!roster.effectiveFrom) {
      setRoster((prev) => ({ ...prev, error: 'Başlangıç tarihi zorunludur.' }));
      return;
    }
    if (roster.effectiveTo && roster.effectiveTo < roster.effectiveFrom) {
      setRoster((prev) => ({
        ...prev,
        error: 'Bitiş tarihi başlangıç tarihinden önce olamaz.',
      }));
      return;
    }

    try {
      setRoster((prev) => ({ ...prev, submitting: true, error: '', results: null }));
      const result = await bulkAssignRoster(branchId, roster.shiftId, {
        employeeIds: roster.employeeIds,
        shiftId: roster.shiftId,
        effectiveFrom: roster.effectiveFrom,
        effectiveTo: roster.effectiveTo || null,
      });
      const okCount = result.results.filter((r) => r.ok).length;
      const failCount = result.results.length - okCount;
      if (failCount === 0) {
        toast.success('Tüm atamalar başarıyla yapıldı.');
      } else {
        toast.error(`${okCount} başarılı, ${failCount} başarısız atama.`);
      }
      setRoster((prev) => ({ ...prev, submitting: false, results: result.results }));
    } catch (err) {
      console.error('Toplu atama başarısız:', err);
      setRoster((prev) => ({
        ...prev,
        submitting: false,
        error: 'Toplu atama sırasında bir hata oluştu. Lütfen tekrar deneyin.',
      }));
    }
  };

  const rosterResultLabel = (errorRef?: string): string => {
    if (errorRef === 'A071') return 'Tarih aralığı çakışıyor';
    return errorRef ? `Hata (${errorRef})` : 'Başarısız';
  };

  // ----- Render -----

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h6">Vardiyalar</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Yenile">
            <IconButton onClick={loadShifts}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<GroupsIcon />} onClick={openRoster}>
            Toplu Atama
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Yeni Vardiya
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {!error && shifts.length === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
              <ScheduleIcon color="disabled" sx={{ fontSize: 48 }} />
              <Typography color="text.secondary">
                Bu şube için henüz vardiya tanımlanmamış.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={openCreate}
                sx={{ mt: 1 }}
              >
                İlk Vardiyayı Oluştur
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Stack spacing={2}>
        {shifts.map((shift) => (
          <Card key={shift.id} variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                  <ScheduleIcon color="action" />
                  <Typography variant="subtitle1">
                    {shift.isDefault && !shift.name ? 'Varsayılan vardiya' : shift.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={SCOPE_LABELS[shift.scope]}
                    color={shift.scope === 'SHARED' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                  {shift.isDefault && (
                    <Chip size="small" label="Varsayılan" color="success" />
                  )}
                </Stack>

                <Stack direction="row" spacing={0.5}>
                  <Tooltip title="Düzenle">
                    <IconButton onClick={() => openEdit(shift)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  {!shift.isDefault && (
                    <Tooltip title="Sil">
                      <IconButton color="error" onClick={() => openDelete(shift)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Oluştur / Düzenle Dialog */}
      <Dialog open={form.open} onClose={closeForm} maxWidth="md" fullWidth>
        <DialogTitle>
          {form.shift
            ? isDefaultForm
              ? 'Varsayılan Vardiyayı Düzenle'
              : 'Vardiyayı Düzenle'
            : 'Yeni Vardiya'}
        </DialogTitle>
        {form.submitting && <LinearProgress />}
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {form.error && <Alert severity="error">{form.error}</Alert>}

            <TextField
              label={isDefaultForm ? 'Varsayılan vardiya' : 'Vardiya Adı'}
              value={form.name}
              onChange={handleNameChange}
              fullWidth
              disabled={isDefaultForm || form.submitting}
              required={!isDefaultForm}
            />

            <FormControl fullWidth disabled={isDefaultForm || form.submitting}>
              <InputLabel id="shift-scope-label">Kapsam</InputLabel>
              <Select
                labelId="shift-scope-label"
                label="Kapsam"
                value={form.scope}
                onChange={handleScopeChange}
              >
                <MenuItem value="SHARED">Paylaşılan</MenuItem>
                <MenuItem value="PERSONAL">Kişisel</MenuItem>
              </Select>
            </FormControl>

            <WorkingHoursGrid
              value={form.workingHours}
              onChange={handleHoursChange}
              disabled={form.submitting}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeForm} disabled={form.submitting}>
            İptal
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={form.submitting}>
            {form.submitting ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Onay Dialog */}
      <Dialog open={deleteState.open} onClose={closeDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Vardiyayı Sil</DialogTitle>
        {deleteState.submitting && <LinearProgress />}
        <DialogContent>
          <Typography>
            {deleteState.shift
              ? `"${deleteState.shift.name}" vardiyasını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeDelete} disabled={deleteState.submitting}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteState.submitting}
          >
            {deleteState.submitting ? 'Siliniyor...' : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toplu Atama Dialog */}
      <Dialog open={roster.open} onClose={closeRoster} maxWidth="sm" fullWidth>
        <DialogTitle>Toplu Vardiya Atama</DialogTitle>
        {roster.submitting && <LinearProgress />}
        <DialogContent dividers>
          <Stack spacing={3} sx={{ pt: 1 }}>
            {roster.error && <Alert severity="error">{roster.error}</Alert>}

            <FormControl fullWidth disabled={roster.submitting}>
              <InputLabel id="roster-shift-label">Vardiya</InputLabel>
              <Select
                labelId="roster-shift-label"
                label="Vardiya"
                value={roster.shiftId}
                onChange={handleRosterShiftChange}
              >
                {shifts.map((shift) => (
                  <MenuItem key={shift.id} value={shift.id}>
                    {shift.isDefault && !shift.name ? 'Varsayılan vardiya' : shift.name}
                    {` (${SCOPE_LABELS[shift.scope]})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={roster.submitting || employeesLoading}>
              <InputLabel id="roster-employees-label">Çalışanlar</InputLabel>
              <Select
                labelId="roster-employees-label"
                multiple
                value={roster.employeeIds}
                onChange={handleRosterEmployeesChange}
                input={<OutlinedInput label="Çalışanlar" />}
                renderValue={(selected) =>
                  (selected as string[]).map((id) => employeeName(id)).join(', ')
                }
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    <Checkbox checked={roster.employeeIds.indexOf(emp.id) > -1} />
                    <ListItemText primary={`${emp.firstName} ${emp.lastName}`} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Başlangıç"
              type="date"
              value={roster.effectiveFrom}
              onChange={(e) =>
                setRoster((prev) => ({ ...prev, effectiveFrom: e.target.value }))
              }
              disabled={roster.submitting}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label="Bitiş (opsiyonel)"
              type="date"
              value={roster.effectiveTo}
              onChange={(e) =>
                setRoster((prev) => ({ ...prev, effectiveTo: e.target.value }))
              }
              disabled={roster.submitting}
              InputLabelProps={{ shrink: true }}
              helperText="Boş bırakılırsa atama süresizdir."
              fullWidth
            />

            {roster.results && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Sonuçlar
                </Typography>
                <List dense disablePadding>
                  {roster.results.map((r) => (
                    <ListItem key={r.employeeId} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {r.ok ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={employeeName(r.employeeId)}
                        secondary={r.ok ? 'Atandı' : rosterResultLabel(r.errorRef)}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeRoster} disabled={roster.submitting}>
            {roster.results ? 'Kapat' : 'İptal'}
          </Button>
          <Button
            variant="contained"
            onClick={handleRosterSubmit}
            disabled={roster.submitting}
          >
            {roster.submitting ? 'Atanıyor...' : 'Ata'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BranchShiftsTab;
